<template>
  <div class="user-reports-container">
    <div class="box">
      <h3 class="title is-5 has-text-info">Reporti koje korisnik vidi</h3>
      <p class="subtitle is-6">
        Odaberite koje reporte ovaj korisnik može vidjeti u aplikaciji.
      </p>

      <!-- Loading indicator -->
      <div v-if="loading" class="has-text-centered">
        <span class="icon is-large">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        <p>Učitavanje...</p>
      </div>

      <!-- Error message -->
      <div v-if="errorMessage" class="notification is-danger is-light">
        {{ errorMessage }}
      </div>

      <!-- Success message -->
      <div v-if="successMessage" class="notification is-success is-light">
        {{ successMessage }}
      </div>

      <!-- Existing user reports -->
      <div v-if="!loading && userReports.length > 0" class="mb-4">
        <h4 class="title is-6">Dodijeljeni reporti</h4>
        <table class="table is-fullwidth is-striped is-hoverable">
          <thead>
            <tr>
              <th>Report</th>
              <th>Opis</th>
              <th>Napomena</th>
              <th>Akcije</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ur in userReports" :key="ur.userReportId">
              <td>{{ ur.reportName }}</td>
              <td>{{ ur.reportDescription || "-" }}</td>
              <td>{{ ur.description || "-" }}</td>
              <td>
                <button
                  class="button is-small is-danger is-light"
                  @click="confirmDeleteUserReport(ur)"
                  title="Ukloni"
                >
                  <span class="icon">
                    <font-awesome-icon icon="trash-alt" />
                  </span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- No reports message -->
      <div
        v-if="!loading && userReports.length === 0"
        class="notification is-warning is-light"
      >
        Korisnik nema dodijeljenih reporta. Dodajte barem jedan report.
      </div>

      <hr />

      <!-- Add report form -->
      <div class="add-report-form">
        <h4 class="title is-6">Dodaj novi report</h4>

        <!-- Report selection -->
        <div class="field">
          <label class="label">Report</label>
          <div class="control">
            <vue-select
              class="has-background-info-light"
              label="name"
              :clearable="false"
              :options="availableReportsFiltered"
              v-model="selectedReport"
              placeholder="Odaberite report..."
            />
          </div>
        </div>

        <!-- Description (optional) -->
        <div class="field">
          <label class="label">Napomena (opcionalno)</label>
          <div class="control">
            <input
              class="input has-background-info-light"
              type="text"
              v-model="formData.description"
              placeholder="Napomena..."
            />
          </div>
        </div>

        <!-- Action buttons -->
        <div class="field is-grouped">
          <div class="control">
            <button
              class="button is-info"
              :disabled="!canSave"
              :class="{ 'is-loading': saving }"
              @click="addUserReport"
            >
              Dodaj report
            </button>
          </div>
        </div>

        <!-- Quick add all reports -->
        <div class="field mt-4" v-if="availableReportsFiltered.length > 1">
          <button
            class="button is-success is-light is-small"
            :class="{ 'is-loading': savingAll }"
            @click="addAllReports"
            :disabled="availableReportsFiltered.length === 0"
          >
            <span class="icon">
              <font-awesome-icon icon="plus" />
            </span>
            <span
              >Dodaj sve dostupne reporte ({{
                availableReportsFiltered.length
              }})</span
            >
          </button>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <div class="modal" :class="{ 'is-active': showDeleteModal }">
      <div class="modal-background" @click="showDeleteModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Potvrda brisanja</p>
          <button
            class="delete"
            aria-label="close"
            @click="showDeleteModal = false"
          ></button>
        </header>
        <section class="modal-card-body">
          <p>
            Da li ste sigurni da želite ukloniti report
            <strong>{{
              reportToDelete ? reportToDelete.reportName : ""
            }}</strong>
            od ovog korisnika?
          </p>
        </section>
        <footer class="modal-card-foot">
          <button
            class="button is-danger"
            :class="{ 'is-loading': deleting }"
            @click="deleteUserReport"
          >
            Obriši
          </button>
          <button class="button" @click="showDeleteModal = false">
            Odustani
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import UserService from "../../services/user.service";

export default {
  name: "EditUserReports",

  props: {
    userId: {
      type: Number,
      required: true,
    },
    availableReports: {
      type: Array,
      default: function () {
        return [];
      },
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      savingAll: false,
      deleting: false,
      userReports: [],
      selectedReport: null,
      formData: {
        userReportId: null,
        userId: null,
        reportId: null,
        value: null,
        description: "",
      },
      errorMessage: "",
      successMessage: "",
      showDeleteModal: false,
      reportToDelete: null,
    };
  },

  computed: {
    availableReportsFiltered() {
      // Filter out reports already assigned to user
      var assignedReportIds = this.userReports.map(function (ur) {
        return ur.reportId;
      });
      return this.availableReports.filter(function (r) {
        return assignedReportIds.indexOf(r.reportId) === -1;
      });
    },
    canSave() {
      return this.selectedReport !== null && !this.saving;
    },
  },

  watch: {
    userId: {
      immediate: true,
      handler: function (newVal) {
        if (newVal) {
          this.loadUserReports();
        }
      },
    },
  },

  mounted: function () {
    this.formData.userId = this.userId;
  },

  methods: {
    loadUserReports: function () {
      var self = this;
      if (!this.userId) return;

      this.loading = true;
      this.errorMessage = "";

      UserService.getUserReportsByUserId(this.userId)
        .then(function (response) {
          self.userReports = response.data;
        })
        .catch(function (error) {
          self.errorMessage = self.getErrorMessage(error);
        })
        .finally(function () {
          self.loading = false;
        });
    },

    addUserReport: function () {
      var self = this;
      this.saving = true;
      this.errorMessage = "";
      this.successMessage = "";

      var userReportData = {
        userId: this.userId,
        reportId: this.selectedReport.reportId,
        value: this.formData.value,
        description: this.formData.description,
      };

      UserService.createUserReport(userReportData)
        .then(function () {
          self.successMessage = "Report uspješno dodan.";
          self.loadUserReports();
          self.resetForm();

          setTimeout(function () {
            self.successMessage = "";
          }, 3000);
        })
        .catch(function (error) {
          self.errorMessage = self.getErrorMessage(error);
        })
        .finally(function () {
          self.saving = false;
        });
    },

    addAllReports: function () {
      var self = this;
      this.savingAll = true;
      this.errorMessage = "";
      this.successMessage = "";

      var userReportsData = this.availableReportsFiltered.map(function (r) {
        return {
          userId: self.userId,
          reportId: r.reportId,
          value: null,
          description: "",
        };
      });

      UserService.createUserReportsBulk(userReportsData)
        .then(function (response) {
          self.successMessage =
            "Uspješno dodano " + response.data.Created + " reporta.";
          self.loadUserReports();
          self.resetForm();

          setTimeout(function () {
            self.successMessage = "";
          }, 3000);
        })
        .catch(function (error) {
          self.errorMessage = self.getErrorMessage(error);
        })
        .finally(function () {
          self.savingAll = false;
        });
    },

    confirmDeleteUserReport: function (ur) {
      this.reportToDelete = ur;
      this.showDeleteModal = true;
    },

    deleteUserReport: function () {
      var self = this;
      if (!this.reportToDelete) return;

      this.deleting = true;
      this.errorMessage = "";

      UserService.deleteUserReport(this.reportToDelete.userReportId)
        .then(function () {
          self.successMessage = "Report uspješno uklonjen.";
          self.loadUserReports();
          self.showDeleteModal = false;
          self.reportToDelete = null;

          setTimeout(function () {
            self.successMessage = "";
          }, 3000);
        })
        .catch(function (error) {
          self.errorMessage = self.getErrorMessage(error);
        })
        .finally(function () {
          self.deleting = false;
        });
    },

    resetForm: function () {
      this.formData = {
        userReportId: null,
        userId: this.userId,
        reportId: null,
        value: null,
        description: "",
      };
      this.selectedReport = null;
    },

    getErrorMessage: function (error) {
      return (
        (error.response && error.response.data) ||
        error.message ||
        error.toString()
      );
    },
  },
};
</script>

<style scoped>
.user-reports-container {
  margin-top: 1rem;
}

.add-report-form {
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 6px;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mt-4 {
  margin-top: 1rem;
}
</style>
